import type { GameStateManager } from '../game/GameState';
import type { GameData, Researcher, Anomaly, ResearchTypeId, LocationId, PropertyType, ResearchCompletionResult, BreachedAnomaly, ActionCompletionToast } from '../game/types';
import { ResourceSystem } from './ResourceSystem';
import { CELL_LOCATIONS } from '../utils/Constants';
import { getAnomalyDisplayName, getAnomalyDescription } from '../utils/AnomalyNameResolver';

export class ResearchSystem {
  constructor(
    private gameState: GameStateManager,
    private gameData: GameData,
    private resourceSystem: ResourceSystem
  ) {}

  canResearch(researcher: Researcher, cellId: LocationId): boolean {
    // Must be at the cell location
    if (researcher.location !== cellId) return false;

    // Must not be currently doing another action
    if (researcher.currentAction !== null && researcher.currentAction !== 'Research') return false;

    // Cell must have an anomaly (not breached)
    const anomaly = this.gameState.getAnomalyInCell(cellId);
    if (!anomaly) return false;

    // Cannot research if cell is the ORIGIN of a breach (anomaly escaped from here)
    const breachedFromHere = this.gameState.getBreachedFromCell(cellId);
    if (breachedFromHere) return false;

    // Check if another researcher is already researching this cell
    const researchers = this.gameState.getResearchers();
    const otherResearcher = researchers.find(r => 
      r.id !== researcher.id && 
      r.currentAction === 'Research' && 
      r.location === cellId
    );
    if (otherResearcher) return false;

    return true;
  }

  calculateResearchTime(selectedTypes: ResearchTypeId[]): number {
    let totalTime = 0;
    for (const typeId of selectedTypes) {
      const researchType = this.gameData.researchTypes.find(rt => rt.id === typeId);
      if (researchType) {
        const timeCost = researchType.costs.find(c => c.time !== undefined);
        if (timeCost?.time) {
          totalTime += timeCost.time;
        }
      }
    }
    return totalTime;
  }

  calculateResearchCosts(selectedTypes: ResearchTypeId[]): { budget: number; chemicals: number; computation: number } {
    const allCosts = selectedTypes.flatMap(typeId => {
      const researchType = this.gameData.researchTypes.find(rt => rt.id === typeId);
      return researchType?.costs || [];
    });

    return this.resourceSystem.calculateTotalCosts(allCosts);
  }

  // Helper: Count occurrences of a property in a list
  countProperty(properties: PropertyType[], prop: PropertyType): number {
    return properties.filter(p => p === prop).length;
  }

  // Helper: Calculate minimum research types required based on Antiepistemic
  calculateMinimumResearchTypes(knownProperties: PropertyType[]): number {
    const antiepistemicCount = this.countProperty(knownProperties, 'Antiepistemic');
    const baseMinimum = 3;
    const minimum = baseMinimum + antiepistemicCount;
    // Cap at 6 (all research types)
    return Math.min(minimum, 6);
  }

  // Helper: Calculate reward reduction multiplier based on Dormant count
  calculateRewardReduction(dormantCount: number): number {
    // Each Dormant reduces by 1/3, rounded up
    // 0 Dormant: 1.0 (full reward)
    // 1 Dormant: 2/3 (reduced by 1/3)
    // 2 Dormant: 1/3 (reduced by 2/3)
    // 3 Dormant: 0.0 (no reward)
    const reduction = (dormantCount * 1) / 3;
    return Math.max(0, 1 - reduction);
  }

  // Helper: Check if researcher has correct silver bullet protection
  checkSilverBulletProtection(researcher: Researcher, anomaly: Anomaly): boolean {
    if (!researcher.silverBullet) return false;

    const silverBullet = this.gameData.silverBullets.find(sb => sb.id === researcher.silverBullet);
    if (!silverBullet) return false;

    // Check if anomaly has the property that the silver bullet is effective against
    return anomaly.knownProperties.includes(silverBullet.effective_property);
  }

  startResearch(researcher: Researcher, cellId: LocationId, selectedTypes: ResearchTypeId[]): boolean {
    if (!this.canResearch(researcher, cellId)) return false;

    const anomaly = this.gameState.getAnomalyInCell(cellId);
    if (!anomaly) return false;

    // Check silver bullet requirement first (before spending resources)
    if (selectedTypes.includes('Neutralize')) {
      if (!researcher.silverBullet) {
        return false; // Neutralize requires a silver bullet
      }
    }

    // Calculate costs (excluding silver bullet check since we handled it above)
    const allCosts = selectedTypes.flatMap(typeId => {
      const researchType = this.gameData.researchTypes.find(rt => rt.id === typeId);
      return researchType?.costs || [];
    }).filter(cost => cost.silver_bullets === undefined); // Filter out silver bullet cost for ResourceSystem

    // Check and spend resources (this also reserves computing)
    if (!this.resourceSystem.spendCosts(allCosts)) {
      return false;
    }

    // Consume silver bullet if Neutralize is selected
    if (selectedTypes.includes('Neutralize')) {
      researcher.silverBullet = null; // Consumed
    }

    // Set research action and store research types for later completion
    const totalTime = this.calculateResearchTime(selectedTypes);
    researcher.currentAction = 'Research';
    researcher.actionTimer = totalTime;
    researcher.actionTotalTime = totalTime;
    researcher.researchTypes = selectedTypes; // Store for completion

    return true;
  }

  completeResearch(researcher: Researcher, cellId: LocationId): void {
    const anomaly = this.gameState.getAnomalyInCell(cellId);
    if (!anomaly || !researcher.researchTypes) return;

    const usedTypes = researcher.researchTypes;
    const config = this.gameData.gameConfig;

    // Release computing - only release what was actually used
    const allCosts = usedTypes.flatMap(typeId => {
      const researchType = this.gameData.researchTypes.find(rt => rt.id === typeId);
      return researchType?.costs || [];
    });
    
    let totalComputation = 0;
    for (const cost of allCosts) {
      if (cost.computation !== undefined) {
        totalComputation += cost.computation;
      }
    }
    if (totalComputation > 0) {
      this.gameState.releaseComputing(totalComputation);
    }

    // Calculate minimum research types BEFORE revealing new property
    const minimumBefore = this.calculateMinimumResearchTypes(anomaly.knownProperties);
    const usedCount = usedTypes.length;

    // Store previous property count BEFORE revealing
    const previousPropertyCount = anomaly.knownProperties.length;

    // Reveal a property if not all known
    let newlyRevealedProperty: PropertyType | null = null;
    const wasFullyResearched = anomaly.knownProperties.length >= 3;
    
    if (!wasFullyResearched) {
      // Count how many times each property appears in data vs known
      const propertyCounts = new Map<PropertyType, number>();
      const knownCounts = new Map<PropertyType, number>();
      
      // Count in data.properties
      for (const prop of anomaly.data.properties) {
        propertyCounts.set(prop, (propertyCounts.get(prop) || 0) + 1);
      }
      
      // Count in knownProperties
      for (const prop of anomaly.knownProperties) {
        knownCounts.set(prop, (knownCounts.get(prop) || 0) + 1);
      }
      
      // Find first property that has more occurrences in data than known
      for (const prop of anomaly.data.properties) {
        const dataCount = propertyCounts.get(prop) || 0;
        const knownCount = knownCounts.get(prop) || 0;
        if (knownCount < dataCount) {
          newlyRevealedProperty = prop;
          anomaly.knownProperties.push(prop);
          break;
        }
      }
    }

    // Calculate minimum AFTER revealing (for Antiepistemic check)
    const minimumAfter = this.calculateMinimumResearchTypes(anomaly.knownProperties);
    const isFullyResearched = anomaly.knownProperties.length >= 3;

    // Store researcher name BEFORE potentially removing them
    const researcherName = researcher.data.name;

    // Get anomaly display name AFTER revealing (so it reflects the new property count)
    const anomalyDisplayName = getAnomalyDisplayName(anomaly, this.gameData);
    const anomalyDescription = getAnomalyDescription(anomaly, this.gameData);

    // Initialize completion result
    const completionResult: ResearchCompletionResult = {
      researcherId: researcher.id,
      researcherName: researcherName,
      cellId,
      usedTypes,
      newlyRevealedProperty,
      allKnownProperties: [...anomaly.knownProperties],
      isFullyResearched,
      wasFullyResearchedBefore: wasFullyResearched,
      warpUnlocked: false,
      anomalyDisplayName,
      anomalyDescription,
      previousPropertyCount,
      effects: {
        lethalBlocked: false,
        lethalKilled: false,
        memeticSanityLoss: 0,
        antiepistemicRewardSkipped: false,
        antiepistemicReason: '',
        hyperrealAnchoringReduction: 0,
        dormantRewardReduction: 0,
        breachingEscaped: false,
        breachingCellId: null
      },
      rewards: {
        smallReward: config.research_reward_small,
        largeReward: config.research_reward_large,
        actualSmallReward: 0,
        actualLargeReward: 0
      }
    };

    // Apply property effects (only revealed properties affect)
    const revealedProperties = anomaly.knownProperties;

    // 1. Lethal effect - ONLY if Lethal is in revealed properties
    const lethalCount = this.countProperty(revealedProperties, 'Lethal');
    const hasTouch = usedTypes.includes('Touch');
    const hasTwoLethal = lethalCount >= 2;

    // Only apply Lethal effect if Lethal is actually a revealed property
    if (lethalCount > 0 && (hasTouch || hasTwoLethal)) {
      const isProtected = this.checkSilverBulletProtection(researcher, anomaly);
      completionResult.effects.lethalBlocked = isProtected;
      
      if (!isProtected) {
        // Kill researcher
        completionResult.effects.lethalKilled = true;
        this.gameState.removeResearcher(researcher.id);
        // Note: researcher is now dead, but we continue processing for the research result
      }
    }

    // 2. Memetic effect (only if Communicate was used)
    if (usedTypes.includes('Communicate')) {
      const memeticCount = this.countProperty(revealedProperties, 'Memetic');
      const sanityLoss = memeticCount * 20;
      completionResult.effects.memeticSanityLoss = sanityLoss;
      
      // Apply sanity loss to researcher (if still alive)
      const aliveResearcher = this.gameState.getResearcher(researcher.id);
      if (aliveResearcher) {
        aliveResearcher.currentSanity = Math.max(0, aliveResearcher.currentSanity - sanityLoss);
      }
    }

    // 3. Antiepistemic effect - check if requirement increased
    if (minimumAfter > minimumBefore && usedCount === minimumBefore) {
      // Requirement increased and player used exact minimum - skip reward
      completionResult.effects.antiepistemicRewardSkipped = true;
      completionResult.effects.antiepistemicReason = `Antiepistemic property revealed increased minimum requirement from ${minimumBefore} to ${minimumAfter}, but only ${usedCount} research types were used.`;
    }

    // 4. Hyperreal effect
    const hyperrealCount = this.countProperty(revealedProperties, 'Hyperreal');
    if (hyperrealCount > 0) {
      const reduction = hyperrealCount;
      completionResult.effects.hyperrealAnchoringReduction = reduction;
      this.gameState.modifyAnchoring(-reduction);
    }

    // 5. Dormant effect - calculate reward reduction
    const dormantCount = this.countProperty(revealedProperties, 'Dormant');
    const rewardMultiplier = this.calculateRewardReduction(dormantCount);
    completionResult.effects.dormantRewardReduction = dormantCount;

    // 6. Breaching effect
    const breachingCount = this.countProperty(revealedProperties, 'Breaching');
    if (breachingCount > 0) {
      // Find empty cell, excluding the origin cell to ensure it goes to a different cell
      const emptyCells = CELL_LOCATIONS.filter(cid => {
        if (cid === cellId) return false; // Exclude origin cell
        const hasAnomaly = this.gameState.getAnomalyInCell(cid) !== undefined;
        const hasBreached = this.gameState.getBreachedInCell(cid) !== undefined;
        return !hasAnomaly && !hasBreached;
      });

      let breachCellId: LocationId;
      if (emptyCells.length > 0) {
        // Random empty cell (different from origin)
        breachCellId = emptyCells[Math.floor(Math.random() * emptyCells.length)];
      } else {
        // All other cells have anomalies, escape in same cell as fallback
        breachCellId = cellId;
      }

      // Create breached anomaly
      const breached: BreachedAnomaly = {
        originalAnomaly: anomaly,
        originCellId: cellId,
        breachCellId,
        escapeTimer: config.breaching_escape_timer,
        isContaining: false,
        containmentResearcherId: null,
        shouldRemoveAfterContainment: wasFullyResearched // Only remove if this was final research (4th+)
      };

      this.gameState.addBreachedAnomaly(breached);
      completionResult.effects.breachingEscaped = true;
      completionResult.effects.breachingCellId = breachCellId;
    }

    // Calculate and award rewards
    if (!completionResult.effects.antiepistemicRewardSkipped) {
      if (wasFullyResearched) {
        // Large reward - anomaly was already fully researched, this is the 4th+ research
        const actualLargeReward = Math.floor(config.research_reward_large * rewardMultiplier);
        completionResult.rewards.actualLargeReward = actualLargeReward;
        
        // Only unlock Warp if we actually received budget (not 0 due to Dormant)
        const wasWarpUnlocked = !this.gameState.isWarpUnlocked() && actualLargeReward > 0;
        if (wasWarpUnlocked) {
          this.gameState.setWarpUnlocked(true);
          completionResult.warpUnlocked = true;
        }
        
        this.gameState.addBudget(actualLargeReward);
        
        // Remove anomaly (but breached anomaly remains if it escaped)
        if (!completionResult.effects.breachingEscaped) {
          this.gameState.removeAnomaly(anomaly);
        }
      } else if (isFullyResearched && !wasFullyResearched) {
        // Just revealed the 3rd property - give minor reward
        const actualSmallReward = Math.floor(config.research_reward_small * rewardMultiplier);
        completionResult.rewards.actualSmallReward = actualSmallReward;
        this.gameState.addBudget(actualSmallReward);
      } else if (!isFullyResearched) {
        // Small reward for revealing a property (1st or 2nd)
        const actualSmallReward = Math.floor(config.research_reward_small * rewardMultiplier);
        completionResult.rewards.actualSmallReward = actualSmallReward;
        this.gameState.addBudget(actualSmallReward);
      }
    }

    // Queue completion result for modal
    this.gameState.queueResearchCompletion(completionResult);

    // Generate toast
    const strings = this.gameData.uiStrings;
    const propertySymbol = strings.propertySymbols?.[completionResult.newlyRevealedProperty?.toLowerCase() || ''] || '';
    const propertyName = strings.properties?.[completionResult.newlyRevealedProperty?.toLowerCase() || ''] || completionResult.newlyRevealedProperty || '';
    
    let summary = '';
    if (completionResult.newlyRevealedProperty) {
      summary = `${anomaly.data.name} - ${propertySymbol} ${propertyName} revealed`;
    } else {
      summary = `${anomaly.data.name} - Research complete`;
    }
    
    if (completionResult.rewards.actualLargeReward > 0) {
      summary += `, +${completionResult.rewards.actualLargeReward} Budget`;
    } else if (completionResult.rewards.actualSmallReward > 0) {
      summary += `, +${completionResult.rewards.actualSmallReward} Budget`;
    }
    
    if (completionResult.effects.lethalKilled) {
      summary += `, Researcher killed`;
    } else if (completionResult.effects.memeticSanityLoss > 0) {
      summary += `, -${completionResult.effects.memeticSanityLoss} Sanity`;
    }
    
    const toast: ActionCompletionToast = {
      researcherName: researcherName,
      location: cellId,
      actionType: 'Research',
      summary: summary,
      timestamp: Date.now()
    };
    this.gameState.queueToast(toast);

    // Clear action and research types (if researcher still alive)
    const aliveResearcher = this.gameState.getResearcher(researcher.id);
    if (aliveResearcher) {
      aliveResearcher.currentAction = null;
      aliveResearcher.actionTimer = 0;
      aliveResearcher.actionTotalTime = 0;
      aliveResearcher.researchTypes = undefined;
    }
  }
}


